import React, { useState, useEffect, useRef, useMemo } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  LayoutDashboard, BookOpen, FileText, Package, Receipt,
  Calculator, BarChart3, Users, ShieldCheck, Truck,
  Settings, CreditCard, Building2, ClipboardList, LogOut, FolderOpen, Landmark,
  Wallet, BadgeIndianRupee, Zap, Upload, Download, Bell, Brain, Database,
  Plug, Paintbrush, Rocket, Link2, QrCode, GitCompare, Bot, Shield, Briefcase,
  User, Smartphone, Sparkles, Search, Mic, Gift, Award, Megaphone, IndianRupee,
  Target, ShoppingBag, GraduationCap, MessageSquare, Mail, Lightbulb, MessageSquarePlus,
  Activity, ArrowRightLeft, Star, ChevronDown, ChevronRight, ChevronUp, ChevronLeft,
  X, Pin, PinOff, Plus, PlusCircle, RefreshCw, Monitor, Code, Check, CheckCircle2,
  PanelLeftClose, PanelLeft, SearchX, Compass, HelpCircle
} from "lucide-react";
import { NavLink } from "@/components/NavLink";
import { useAuth } from "@/contexts/AuthContext";
import { ROLE_NAV_CONFIG, NavItem, NavGroup, UserRole } from "@/lib/roles";
import { CompanySwitcher } from "@/components/CompanySwitcher";
import { useNavPreferences } from "@/hooks/useNavPreferences";
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  useSidebar,
} from "@/components/ui/sidebar";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";

// Complete Icon mapping for all dynamic icons used in navigation
const ICON_MAP: Record<string, React.ComponentType<{ className?: string }>> = {
  LayoutDashboard, BookOpen, FileText, Package, Receipt,
  Calculator, BarChart3, Users, ShieldCheck, Truck,
  Settings, CreditCard, Building2, ClipboardList, FolderOpen, Landmark,
  Wallet, BadgeIndianRupee, Zap, Upload, Download, Bell, Brain, Database,
  Plug, Paintbrush, Rocket, Link2, QrCode, GitCompare, Bot, Shield, Briefcase,
  User, Smartphone, Sparkles, Search, Mic, Gift, Award, Megaphone, IndianRupee,
  Target, ShoppingBag, GraduationCap, MessageSquare, Mail, Lightbulb, MessageSquarePlus,
  Activity, ArrowRightLeft, RefreshCw, Monitor, Code, Compass, HelpCircle
};

// QuickBooks-style Accounting Quick Actions
const QUICK_ACTIONS = [
  { title: "New Sale", url: "/dashboard/sales", icon: Receipt, desc: "Create sales voucher" },
  { title: "New Purchase", url: "/dashboard/purchase", icon: Calculator, desc: "Record purchase entry" },
  { title: "New Invoice", url: "/dashboard/invoices", icon: FileText, desc: "Issue tax invoice" },
  { title: "Receive Payment", url: "/dashboard/journals?type=receipt&action=new", icon: IndianRupee, desc: "Record customer receipt" },
  { title: "Pay Money", url: "/dashboard/journals?type=payment&action=new", icon: CreditCard, desc: "Make vendor payment" },
  { title: "Record Expense", url: "/dashboard/expenses", icon: Wallet, desc: "Record company expense" },
  { title: "Stock Adjustment", url: "/dashboard/journals?type=stock-journal&action=new", icon: ArrowRightLeft, desc: "Adjust inventory quantities" },
];

export function AppSidebar() {
  const { state, setOpen, isMobile, setOpenMobile, toggleSidebar } = useSidebar();
  const collapsed = state === "collapsed";
  const { role, roleLabel, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();

  const {
    favorites,
    expandedGroups,
    isCompact,
    toggleFavorite,
    removeFavorite,
    reorderFavorites,
    toggleGroup,
    expandGroup,
    toggleCompact,
    resetPreferences,
  } = useNavPreferences();

  // Search input state
  const [searchQuery, setSearchQuery] = useState("");
  const [searchSelectedIndex, setSearchSelectedIndex] = useState(0);
  const searchInputRef = useRef<HTMLInputElement>(null);

  // Sync compact preference from backend with shadcn sidebar
  useEffect(() => {
    if (isCompact !== undefined && !isMobile) {
      if (isCompact && !collapsed) {
        setOpen(false);
      } else if (!isCompact && collapsed) {
        setOpen(true);
      }
    }
  }, [isCompact, isMobile]);

  // Handle compact toggle from user button
  const handleToggleCompact = () => {
    toggleCompact();
    toggleSidebar();
  };

  // Navigation configuration for active role
  const navGroups: NavGroup[] = useMemo(() => {
    return ROLE_NAV_CONFIG[role as UserRole] || ROLE_NAV_CONFIG.company_admin;
  }, [role]);

  // Flattened searchable navigation items
  const allNavItems = useMemo(() => {
    const list: { item: NavItem; groupLabel: string; groupId: string }[] = [];
    navGroups.forEach((g) => {
      g.items.forEach((item) => {
        list.push({ item, groupLabel: g.label, groupId: g.id });
      });
    });
    return list;
  }, [navGroups]);

  // Filtered search results
  const searchResults = useMemo(() => {
    if (!searchQuery.trim()) return [];
    const q = searchQuery.toLowerCase().trim();
    return allNavItems.filter(
      (entry) =>
        entry.item.title.toLowerCase().includes(q) ||
        entry.groupLabel.toLowerCase().includes(q)
    );
  }, [searchQuery, allNavItems]);

  // Keyboard shortcut '/' to focus navigation search
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "/" && document.activeElement?.tagName !== "INPUT" && document.activeElement?.tagName !== "TEXTAREA") {
        e.preventDefault();
        if (collapsed) {
          setOpen(true);
        }
        setTimeout(() => {
          searchInputRef.current?.focus();
        }, 100);
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [collapsed, setOpen]);

  // Auto-expand group containing current active route
  useEffect(() => {
    const currentPath = location.pathname;
    const currentSearch = location.search;

    for (const group of navGroups) {
      const match = group.items.some((item) => {
        if (item.url === "/dashboard" && currentPath === "/dashboard") return true;
        if (item.url.includes("?")) {
          const [basePath, baseQuery] = item.url.split("?");
          return currentPath === basePath && currentSearch.includes(baseQuery);
        }
        return currentPath === item.url || (currentPath.startsWith(item.url + "/") && item.url !== "/dashboard");
      });

      if (match) {
        expandGroup(group.id);
        break;
      }
    }
  }, [location.pathname, location.search, navGroups, expandGroup]);

  // Keyboard navigation for search input
  const handleSearchKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (searchResults.length === 0) return;

    if (e.key === "ArrowDown") {
      e.preventDefault();
      setSearchSelectedIndex((prev) => (prev + 1) % searchResults.length);
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setSearchSelectedIndex((prev) => (prev - 1 + searchResults.length) % searchResults.length);
    } else if (e.key === "Enter") {
      e.preventDefault();
      const target = searchResults[searchSelectedIndex];
      if (target) {
        navigate(target.item.url);
        setSearchQuery("");
        if (isMobile) setOpenMobile(false);
      }
    } else if (e.key === "Escape") {
      e.preventDefault();
      setSearchQuery("");
      searchInputRef.current?.blur();
    }
  };

  // Helper to determine if an item is currently active
  const isItemActive = (url: string) => {
    if (url === "/dashboard") {
      return location.pathname === "/dashboard";
    }
    if (url.includes("?")) {
      const [basePath, baseQuery] = url.split("?");
      return location.pathname === basePath && location.search.includes(baseQuery);
    }
    return location.pathname === url || (location.pathname.startsWith(url + "/") && url !== "/dashboard");
  };

  // Helper to check if item is pinned to favorites
  const isPinned = (url: string) => {
    return favorites.some((f) => f.url === url);
  };

  const handleLinkClick = (e?: React.MouseEvent | React.SyntheticEvent) => {
    // Immediately blur clicked element so no focus outline persists
    if (e?.currentTarget && typeof (e.currentTarget as HTMLElement).blur === "function") {
      (e.currentTarget as HTMLElement).blur();
    }
    // Clear any residual focus within the sidebar
    if (
      document.activeElement instanceof HTMLElement &&
      document.activeElement.closest('[data-sidebar="sidebar"], aside, .bg-sidebar')
    ) {
      document.activeElement.blur();
    }
    if (isMobile) {
      setOpenMobile(false);
    }
  };

  // Automatically clear stale sidebar focus on any route change (browser back/forward, in-page links)
  useEffect(() => {
    if (
      document.activeElement instanceof HTMLElement &&
      document.activeElement.closest('[data-sidebar="sidebar"], aside, .bg-sidebar')
    ) {
      document.activeElement.blur();
    }
  }, [location.pathname, location.search]);

  return (
    <Sidebar collapsible="icon" className="border-r border-sidebar-border bg-sidebar select-none font-sans">
      {/* 1. Header / Brand Bar (Unified 56px height with Dashboard header) */}
      <div className="h-14 px-3.5 flex items-center justify-between border-b border-sidebar-border shrink-0 bg-sidebar">
        <div className="flex items-center gap-2.5 min-w-0">
          <div className="w-8 h-8 rounded-lg bg-blue-600 flex items-center justify-center flex-shrink-0 text-white font-bold font-display text-sm shadow-xs">
            A
          </div>
          {!collapsed && (
            <div className="flex flex-col min-w-0">
              <h2 className="text-sm font-bold font-display text-slate-900 tracking-tight leading-tight truncate">
                AccouPro
              </h2>
              <p className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold truncate">
                by hSECURITIES
              </p>
            </div>
          )}
        </div>

        {/* Compact / Expand Toggle Button */}
        {!isMobile && (
          <TooltipProvider delayDuration={200}>
            <Tooltip>
              <TooltipTrigger asChild>
                <button
                  type="button"
                  onClick={(e) => {
                    (e.currentTarget as HTMLElement).blur();
                    handleToggleCompact();
                  }}
                  className="text-slate-400 hover:text-slate-700 hover:bg-slate-100 p-1.5 rounded-lg transition-colors outline-none focus:outline-none focus:ring-0 focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-blue-500"
                  aria-label={collapsed ? "Expand sidebar" : "Compact sidebar"}
                >
                  {collapsed ? <PanelLeft className="h-4 w-4" /> : <PanelLeftClose className="h-4 w-4" />}
                </button>
              </TooltipTrigger>
              <TooltipContent side="right">
                {collapsed ? "Expand sidebar" : "Collapse to icon-only mode"}
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        )}
      </div>

      {/* 2. Company & Role Switcher */}
      {role !== "super_admin" && (
        <div className="px-2.5 py-2 border-b border-sidebar-border shrink-0">
          <CompanySwitcher collapsed={collapsed} />
        </div>
      )}

      {/* 3. Prominent Blue Quick Action Create Button */}
      <div className="px-2.5 pt-2 pb-1 shrink-0">
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            {collapsed ? (
              <button
                className="w-8 h-8 mx-auto flex items-center justify-center rounded-lg bg-blue-600 text-white shadow-xs hover:bg-blue-700 transition-colors cursor-pointer"
                title="Quick Accounting Actions"
              >
                <Plus className="h-4 w-4 stroke-[2.5]" />
              </button>
            ) : (
              <button className="w-full flex items-center justify-between px-3 py-2 rounded-lg bg-blue-600 hover:bg-blue-700 text-white shadow-xs font-semibold text-xs transition-colors cursor-pointer">
                <span className="flex items-center gap-2">
                  <Plus className="h-3.5 w-3.5 stroke-[2.5]" />
                  <span className="tracking-tight">Quick Action</span>
                </span>
                <ChevronDown className="h-3 w-3 opacity-90" />
              </button>
            )}
          </DropdownMenuTrigger>
          <DropdownMenuContent align="start" className="w-64 p-1.5 rounded-xl border border-border bg-popover shadow-xl">
            <DropdownMenuLabel className="text-[10px] uppercase tracking-wider text-muted-foreground font-bold px-2.5 py-1.5">
              Quick Accounting Actions
            </DropdownMenuLabel>
            <DropdownMenuSeparator className="my-1" />
            {QUICK_ACTIONS.map((action) => {
              const ActionIcon = action.icon;
              return (
                <DropdownMenuItem
                  key={action.title}
                  onClick={() => {
                    navigate(action.url);
                    handleLinkClick();
                  }}
                  className="flex items-start gap-2.5 px-2.5 py-2 cursor-pointer hover:bg-muted/80 rounded-lg transition-colors"
                >
                  <div className="w-6 h-6 rounded-md bg-blue-50 text-blue-600 flex items-center justify-center shrink-0 mt-0.5 border border-blue-100">
                    <ActionIcon className="h-3.5 w-3.5" />
                  </div>
                  <div className="flex flex-col min-w-0">
                    <span className="text-xs font-semibold text-foreground truncate">{action.title}</span>
                    <span className="text-[10px] text-muted-foreground truncate">{action.desc}</span>
                  </div>
                </DropdownMenuItem>
              );
            })}
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      {/* 4. Dedicated In-Sidebar Navigation Search Field */}
      {!collapsed && (
        <div className="px-2.5 pt-1.5 pb-2 shrink-0">
          <div className="relative flex items-center">
            <Search className="absolute left-2.5 h-3.5 w-3.5 text-slate-400 pointer-events-none" />
            <input
              ref={searchInputRef}
              type="text"
              value={searchQuery}
              onChange={(e) => {
                setSearchQuery(e.target.value);
                setSearchSelectedIndex(0);
              }}
              onKeyDown={handleSearchKeyDown}
              placeholder="Search navigation... (/)"
              className="w-full h-8 pl-8 pr-7 text-xs bg-slate-50 text-slate-900 placeholder:text-slate-400 rounded-lg border border-slate-200 focus:border-blue-500 focus:bg-white focus:outline-none focus:ring-1 focus:ring-blue-500 transition-colors font-sans"
            />
            {searchQuery ? (
              <button
                onClick={() => setSearchQuery("")}
                className="absolute right-2 text-slate-400 hover:text-slate-700 p-0.5"
                title="Clear search"
              >
                <X className="h-3.5 w-3.5" />
              </button>
            ) : (
              <kbd className="absolute right-2 text-[10px] px-1.5 py-0.5 bg-slate-200/60 text-slate-500 border border-slate-200 rounded font-mono shadow-2xs">
                /
              </kbd>
            )}
          </div>
        </div>
      )}

      {/* 5. Independent Scrollable Navigation Area */}
      <SidebarContent className="px-2.5 py-1.5 sidebar-scrollbar flex-1 overflow-y-auto overflow-x-hidden space-y-2.5">
        {/* A. Search Active Filtered Results */}
        {searchQuery.trim() ? (
          <div className="space-y-1">
            <div className="flex items-center justify-between px-2 py-1 text-[10px] uppercase tracking-wider text-slate-500 font-bold">
              <span>Matching Modules ({searchResults.length})</span>
              <button
                onClick={() => setSearchQuery("")}
                className="text-[10px] text-blue-600 hover:underline lowercase font-medium"
              >
                Clear
              </button>
            </div>

            {searchResults.length === 0 ? (
              <div className="p-4 text-center rounded-xl bg-slate-50 border border-slate-200">
                <SearchX className="h-6 w-6 text-slate-400 mx-auto mb-1.5" />
                <p className="text-xs font-semibold text-slate-700">No navigation items found</p>
                <p className="text-[10px] text-slate-500 mt-0.5">
                  Try searching for sales, invoices, gst, ledgers...
                </p>
              </div>
            ) : (
              searchResults.map((result, idx) => {
                const Icon = ICON_MAP[result.item.icon] || LayoutDashboard;
                const isSelected = idx === searchSelectedIndex;
                const active = isItemActive(result.item.url);
                const pinned = isPinned(result.item.url);

                return (
                  <div
                    key={result.item.url + idx}
                    tabIndex={0}
                    className={`group relative flex items-center justify-between px-2.5 py-2 rounded-lg text-xs transition-all cursor-pointer outline-none focus:outline-none focus:ring-0 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-600/30 ${
                      isSelected
                        ? "bg-blue-600 text-white font-medium shadow-2xs border border-blue-600"
                        : active
                        ? "bg-blue-50 text-blue-700 font-semibold border border-blue-200"
                        : "border border-transparent text-slate-600 hover:bg-slate-100 hover:text-slate-900"
                    }`}
                    onClick={(e) => {
                      (e.currentTarget as HTMLElement).blur();
                      navigate(result.item.url);
                      setSearchQuery("");
                      handleLinkClick(e);
                    }}
                  >
                    <div className="flex items-center gap-2.5 min-w-0 pr-8">
                      <Icon className={`h-4 w-4 shrink-0 ${isSelected ? "text-white" : active ? "text-blue-600" : "text-slate-400 group-hover:text-slate-600"}`} />
                      <div className="flex flex-col min-w-0">
                        <span className="truncate font-medium">{result.item.title}</span>
                        <span className={`text-[9px] uppercase tracking-wider truncate ${isSelected ? "text-blue-100" : "text-slate-400"}`}>
                          {result.groupLabel}
                        </span>
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        (e.currentTarget as HTMLElement).blur();
                        toggleFavorite(result.item);
                      }}
                      className="absolute right-2 p-1 hover:bg-slate-200/60 rounded transition-all outline-none focus:outline-none focus:ring-0 focus-visible:ring-1 focus-visible:ring-blue-500/50"
                      title={pinned ? "Unpin favorite" : "Pin favorite"}
                    >
                      <Pin className={`h-3 w-3 ${pinned ? "fill-blue-600 text-blue-600" : "text-slate-400 hover:text-slate-600"}`} />
                    </button>
                  </div>
                );
              })
            )}
          </div>
        ) : (
          <>
            {/* B. Favorites / Pinned Section */}
            {favorites.length > 0 && (
              <div className="rounded-xl bg-slate-50 border border-slate-200/90 p-1.5 shadow-2xs">
                {!collapsed && (
                  <div className="flex items-center justify-between px-2 pt-1 pb-1.5 mb-0.5">
                    <span className="flex items-center gap-1.5 text-[10px] uppercase tracking-wider font-bold text-slate-500">
                      <Star className="h-3.5 w-3.5 text-amber-500 fill-amber-500" />
                      <span>FAVORITES</span>
                      <span className="text-[9px] font-mono px-1.5 py-0.2 rounded-full bg-slate-200/80 text-slate-700 border border-slate-300/60 font-semibold">
                        {favorites.length}
                      </span>
                    </span>
                    <button
                      type="button"
                      onClick={(e) => {
                        (e.currentTarget as HTMLElement).blur();
                        resetPreferences();
                      }}
                      className="text-[10px] text-slate-500 hover:text-slate-800 transition-colors font-medium hover:underline outline-none focus:outline-none focus:ring-0 focus-visible:ring-1 focus-visible:ring-blue-500/50 rounded"
                      title="Reset navigation to default"
                    >
                      Reset
                    </button>
                  </div>
                )}

                <div className="space-y-0.5">
                  {favorites.map((fav, index) => {
                    const Icon = ICON_MAP[fav.icon] || LayoutDashboard;
                    const active = isItemActive(fav.url);

                    return (
                      <div
                        key={fav.url}
                        className="group relative flex items-center justify-between"
                      >
                        <TooltipProvider delayDuration={200}>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <NavLink
                                to={fav.url}
                                end={fav.url === "/dashboard"}
                                data-active={active ? "true" : "false"}
                                onClick={handleLinkClick}
                                onMouseUp={(e) => (e.currentTarget as HTMLElement).blur()}
                                className={`relative flex items-center gap-2.5 w-full px-2.5 py-1.5 rounded-lg text-xs transition-all duration-150 outline-none focus:outline-none focus:ring-0 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-600/30 ${
                                  !collapsed ? "pr-14" : "justify-center px-0 w-8 h-8 mx-auto"
                                } ${
                                  active
                                    ? "bg-blue-50 text-blue-700 font-semibold border border-blue-200/80 shadow-2xs"
                                    : "border border-transparent text-slate-600 hover:bg-slate-100 hover:text-slate-900"
                                }`}
                              >
                                {active && !collapsed && (
                                  <span className="absolute left-0 top-1.5 bottom-1.5 w-1 rounded-r-full bg-blue-600" />
                                )}
                                <Icon className={`h-4 w-4 shrink-0 transition-colors ${
                                  active ? "text-blue-600" : "text-slate-400 group-hover:text-slate-600"
                                }`} />
                                {!collapsed && <span className="truncate flex-1 text-left font-medium">{fav.title}</span>}
                              </NavLink>
                            </TooltipTrigger>
                            {collapsed && (
                              <TooltipContent side="right">
                                <p className="font-semibold text-xs">{fav.title}</p>
                                <p className="text-[10px] text-blue-600 font-medium">★ Pinned Favorite</p>
                              </TooltipContent>
                            )}
                          </Tooltip>
                        </TooltipProvider>

                        {/* Reorder & Unpin Controls on Hover */}
                        {!collapsed && (
                          <div className="absolute right-1.5 flex items-center gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity bg-white border border-slate-200 shadow-sm px-1 py-0.5 rounded">
                            {index > 0 && (
                              <button
                                type="button"
                                onClick={(e) => {
                                  (e.currentTarget as HTMLElement).blur();
                                  reorderFavorites(index, index - 1);
                                }}
                                className="p-0.5 text-slate-500 hover:text-slate-800 hover:bg-slate-100 rounded outline-none focus:outline-none focus:ring-0 focus-visible:ring-1 focus-visible:ring-blue-500/50"
                                title="Move Up"
                              >
                                <ChevronUp className="h-3 w-3" />
                              </button>
                            )}
                            {index < favorites.length - 1 && (
                              <button
                                type="button"
                                onClick={(e) => {
                                  (e.currentTarget as HTMLElement).blur();
                                  reorderFavorites(index, index + 1);
                                }}
                                className="p-0.5 text-slate-500 hover:text-slate-800 hover:bg-slate-100 rounded outline-none focus:outline-none focus:ring-0 focus-visible:ring-1 focus-visible:ring-blue-500/50"
                                title="Move Down"
                              >
                                <ChevronDown className="h-3 w-3" />
                              </button>
                            )}
                            <button
                              type="button"
                              onClick={(e) => {
                                (e.currentTarget as HTMLElement).blur();
                                removeFavorite(fav.url);
                              }}
                              className="p-0.5 text-slate-500 hover:text-rose-600 hover:bg-rose-50 rounded outline-none focus:outline-none focus:ring-0 focus-visible:ring-1 focus-visible:ring-rose-400/50"
                              title="Unpin Favorite"
                            >
                              <PinOff className="h-3 w-3" />
                            </button>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* C. 10 QuickBooks-Style Navigation Groups */}
            <div className="space-y-2">
              {navGroups.map((group) => {
                const isExpanded = expandedGroups.includes(group.id);
                const hasActiveChild = group.items.some((it) => isItemActive(it.url));

                return (
                  <div key={group.id} className="space-y-0.5">
                    {/* Group Accordion Header */}
                    {!collapsed ? (
                      <button
                        type="button"
                        onClick={(e) => {
                          (e.currentTarget as HTMLElement).blur();
                          toggleGroup(group.id);
                        }}
                        className={`flex items-center justify-between w-full px-2.5 py-1.5 rounded-lg text-[10.5px] uppercase tracking-wider font-bold transition-all outline-none focus:outline-none focus:ring-0 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-600/30 ${
                          hasActiveChild
                            ? "text-blue-700 font-extrabold bg-blue-50/70 border border-blue-200/60"
                            : "border border-transparent text-slate-500 hover:text-slate-900 hover:bg-slate-100/80"
                        }`}
                      >
                        <span className="truncate flex items-center gap-2">
                          <span>{group.label}</span>
                          <span className={`text-[9px] font-mono px-1.5 py-0.2 rounded-full font-semibold ${
                            hasActiveChild
                              ? "bg-blue-100 text-blue-700 border border-blue-200"
                              : "bg-slate-100 text-slate-500 border border-slate-200/60"
                          }`}>
                            {group.items.length}
                          </span>
                        </span>
                        <ChevronDown className={`h-3.5 w-3.5 shrink-0 transition-transform duration-200 text-slate-400 group-hover:text-slate-600 ${
                          isExpanded ? "rotate-0" : "-rotate-90"
                        }`} />
                      </button>
                    ) : (
                      <div className="my-2 border-t border-slate-200" />
                    )}

                    {/* Group Items */}
                    {(isExpanded || collapsed) && (
                      <div className={!collapsed ? "space-y-0.5 pl-2 ml-1.5 border-l border-slate-200" : "space-y-1"}>
                        {group.items.map((item) => {
                          const Icon = ICON_MAP[item.icon] || LayoutDashboard;
                          const active = isItemActive(item.url);
                          const pinned = isPinned(item.url);

                          return (
                            <div
                              key={item.title + item.url}
                              className="group relative flex items-center justify-between"
                            >
                              <TooltipProvider delayDuration={200}>
                                <Tooltip>
                                  <TooltipTrigger asChild>
                                    <NavLink
                                      to={item.url}
                                      end={item.url === "/dashboard"}
                                      data-active={active ? "true" : "false"}
                                      onClick={handleLinkClick}
                                      onMouseUp={(e) => (e.currentTarget as HTMLElement).blur()}
                                      className={`relative flex items-center gap-2.5 w-full px-2.5 py-1.5 rounded-lg text-xs transition-all duration-150 outline-none focus:outline-none focus:ring-0 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-600/30 ${
                                        !collapsed ? "pr-8" : "justify-center px-0 w-8 h-8 mx-auto"
                                      } ${
                                        active
                                          ? "bg-blue-50 text-blue-700 font-semibold border border-blue-200/80 shadow-2xs"
                                          : "border border-transparent text-slate-600 hover:text-slate-900 hover:bg-slate-100"
                                      }`}
                                    >
                                      {active && !collapsed && (
                                        <span className="absolute left-0 top-1.5 bottom-1.5 w-1 rounded-r-full bg-blue-600" />
                                      )}
                                      <Icon
                                        className={`h-4 w-4 shrink-0 transition-colors ${
                                          active
                                            ? "text-blue-600 font-semibold"
                                            : "text-slate-400 group-hover:text-slate-600"
                                        }`}
                                      />
                                      {!collapsed && (
                                        <span className="truncate flex-1 text-left">{item.title}</span>
                                      )}
                                    </NavLink>
                                  </TooltipTrigger>
                                  {collapsed && (
                                    <TooltipContent side="right">
                                      <p className="font-semibold text-xs">{item.title}</p>
                                      <p className="text-[10px] text-muted-foreground">{group.label}</p>
                                    </TooltipContent>
                                  )}
                                </Tooltip>
                              </TooltipProvider>

                              {/* Hover Pin Toggle (Safe spacing with pr-8 on link) */}
                              {!collapsed && (
                                <button
                                  type="button"
                                  onClick={(e) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    (e.currentTarget as HTMLElement).blur();
                                    toggleFavorite(item);
                                  }}
                                  className={`absolute right-1.5 p-1 rounded transition-all outline-none focus:outline-none focus:ring-0 focus-visible:ring-1 focus-visible:ring-blue-500/50 ${
                                    pinned
                                      ? "opacity-100 text-blue-600 fill-blue-600"
                                      : "opacity-0 group-hover:opacity-100 text-slate-400 hover:text-slate-700 hover:bg-slate-200/70"
                                  }`}
                                  title={pinned ? "Unpin from favorites" : "Pin to favorites"}
                                >
                                  <Pin className={`h-3 w-3 ${pinned ? "fill-blue-600 text-blue-600" : ""}`} />
                                </button>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </>
        )}
      </SidebarContent>

      {/* 6. Footer / User Role & Logout */}
      <SidebarFooter className="p-3 border-t border-slate-200 shrink-0 bg-slate-50/60">
        {!collapsed && (
          <div className="px-1 py-1 mb-2 flex items-center justify-between text-xs">
            <span className="flex items-center gap-1.5 text-slate-500 text-[11px]">
              <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 shadow-2xs" />
              <span>Role: <strong className="text-slate-800 font-semibold">{roleLabel}</strong></span>
            </span>
            <button
              type="button"
              onClick={(e) => {
                (e.currentTarget as HTMLElement).blur();
                resetPreferences();
              }}
              className="text-[10px] text-slate-500 hover:text-slate-800 hover:underline flex items-center gap-1 transition-colors outline-none focus:outline-none focus:ring-0 focus-visible:ring-1 focus-visible:ring-blue-500/50 rounded"
              title="Reset sidebar to standard layout"
            >
              <RefreshCw className="h-2.5 w-2.5" />
              <span>Reset</span>
            </button>
          </div>
        )}

        <button
          type="button"
          onClick={(e) => {
            (e.currentTarget as HTMLElement).blur();
            logout();
          }}
          className="flex items-center gap-2.5 text-xs font-medium text-rose-600 hover:text-rose-700 hover:bg-rose-50 border border-transparent hover:border-rose-200 transition-all w-full px-2.5 py-2 rounded-lg outline-none focus:outline-none focus:ring-0 focus-visible:ring-2 focus-visible:ring-rose-400/50"
          title="Sign out of AccouPro"
        >
          <LogOut className="h-4 w-4 shrink-0 text-rose-500" />
          {!collapsed && <span>Log Out</span>}
        </button>
      </SidebarFooter>
    </Sidebar>
  );
}
